import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, TrendingUp, TrendingDown, PoundSterling, Home,
  CheckCircle, Loader2, BarChart3, PieChart, Activity, Clock,
  XCircle, Users, Award, Eye, EyeOff
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, AreaChart, Area
} from "recharts";
import { API } from "@/App";

const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];

const SolicitorAnalyticsPage = () => {
  const navigate = useNavigate();
  const { firmId } = useParams();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchAnalytics();
  }, [firmId]);

  const fetchAnalytics = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/solicitors/firm/${encodeURIComponent(firmId)}/analytics`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    if (comparison) {
      setShowComparison(!showComparison);
      return;
    }
    
    try {
      setLoadingComparison(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/solicitors/analytics/comparison`, { headers });
      setComparison(response.data);
      setShowComparison(true);
    } catch (error) {
      console.error("Error fetching comparison:", error);
      toast.error("Failed to load comparison data");
    } finally {
      setLoadingComparison(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `£${(value / 1000000).toFixed(1)}M`;
    }
    return `£${(value / 1000).toFixed(0)}K`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const monthlyData = analytics?.monthly_trends || [];
  const statusData = analytics?.status_breakdown || [];
  const stageDurations = analytics?.stage_durations || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-700 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(`/sales/solicitor/${encodeURIComponent(firmId)}`)}
                className="text-white hover:bg-indigo-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{analytics?.firm_name || 'Firm'} Analytics</h1>
                <p className="text-indigo-200">Performance metrics & trends</p>
              </div>
            </div>
            <Button 
              onClick={fetchComparison}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              disabled={loadingComparison}
            >
              {loadingComparison ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : showComparison ? (
                <EyeOff className="w-4 h-4 mr-2" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              {showComparison ? "Hide Comparison" : "Compare to Others"}
            </Button>
          </div>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Cases</p>
                  <p className="text-2xl font-bold">{analytics?.active_cases || 0}</p>
                </div>
                <Home className="w-8 h-8 text-indigo-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <Badge variant="secondary">{analytics?.exchanged_cases || 0} exchanged</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pipeline Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(analytics?.pipeline_value || 0)}</p>
                </div>
                <PoundSterling className="w-8 h-8 text-blue-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <span className="text-muted-foreground">
                  Avg: {formatCurrency(analytics?.avg_case_value || 0)}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed YTD</p>
                  <p className="text-2xl font-bold">{analytics?.completed_ytd || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <span className="text-emerald-600">
                  {formatCurrency(analytics?.completed_value || 0)} value
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Fall-Through Rate</p>
                  <p className="text-2xl font-bold">{analytics?.fall_through_rate || 0}%</p>
                </div>
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                {(analytics?.fall_through_rate || 0) <= 15 ? (
                  <span className="text-emerald-600 flex items-center">
                    <TrendingDown className="w-4 h-4 mr-1" /> Below average
                  </span>
                ) : (
                  <span className="text-red-600 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" /> Above average
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Comparison Section (when visible) */}
      {showComparison && comparison && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-800">
                <Award className="w-5 h-5" />
                Top 3 Performing Firms (Anonymized)
              </CardTitle>
              <CardDescription>
                Comparing against {comparison.total_firms_analyzed} firms in our network
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {/* Top 3 Firms */}
                {comparison.top_firms?.map((firm, idx) => (
                  <Card key={idx} className="bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : 'bg-amber-700'
                        }`}>
                          {idx + 1}
                        </div>
                        <span className="font-semibold">{firm.name}</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Active Cases</span>
                          <span className="font-medium">{firm.active_cases}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Completed</span>
                          <span className="font-medium">{firm.completed}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Pipeline</span>
                          <span className="font-medium">{formatCurrency(firm.pipeline_value)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Completion Rate</span>
                          <span className="font-medium text-emerald-600">{firm.completion_rate}%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Industry Averages */}
                <Card className="bg-slate-50 border-dashed">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-6 h-6 text-slate-600" />
                      <span className="font-semibold text-slate-700">Industry Avg</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avg Cases/Firm</span>
                        <span className="font-medium">{comparison.industry_averages?.avg_cases_per_firm || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avg Pipeline</span>
                        <span className="font-medium">{formatCurrency(comparison.industry_averages?.avg_pipeline_value || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avg Completion</span>
                        <span className="font-medium">{comparison.industry_averages?.avg_completion_rate || 0}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Monthly Completions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-indigo-600" />
                    Monthly Completions
                  </CardTitle>
                  <CardDescription>Cases completed by month</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="completed" 
                        stroke="#10b981" 
                        fill="#6ee7b7"
                        name="Completed"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="exchanged" 
                        stroke="#3b82f6" 
                        fill="#93c5fd"
                        name="Exchanged"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Status Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-600" />
                    Case Status Breakdown
                  </CardTitle>
                  <CardDescription>Current case distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={statusData.filter(s => s.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Pipeline Value Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-amber-600" />
                    Pipeline Value Trend
                  </CardTitle>
                  <CardDescription>Monthly pipeline value</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                      <Line 
                        type="monotone" 
                        dataKey="pipeline_value" 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        dot={{ fill: '#f59e0b' }}
                        name="Pipeline Value"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Time to Completion */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    Time to Completion
                  </CardTitle>
                  <CardDescription>Average days by stage</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stageDurations} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" unit=" days" />
                      <YAxis dataKey="stage" type="category" width={100} fontSize={11} />
                      <Tooltip formatter={(value) => `${value} days`} />
                      <Bar dataKey="avg_days" fill="#8b5cf6" name="Avg Days" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pipeline Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Pipeline Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Active Pipeline</p>
                        <p className="text-2xl font-bold text-indigo-700">{formatCurrency(analytics?.pipeline_value || 0)}</p>
                      </div>
                      <PoundSterling className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Average Case Value</p>
                        <p className="text-2xl font-bold text-emerald-700">{formatCurrency(analytics?.avg_case_value || 0)}</p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Exchanged Cases</p>
                        <p className="text-2xl font-bold text-blue-700">{analytics?.exchanged_cases || 0}</p>
                      </div>
                      <CheckCircle className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Case Stage Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Status Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {statusData.map((status, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium">{status.name}</span>
                            <span className="text-sm text-muted-foreground">{status.value} cases</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full transition-all"
                              style={{ 
                                width: `${(status.value / Math.max(...statusData.map(s => s.value), 1)) * 100}%`,
                                backgroundColor: COLORS[idx % COLORS.length]
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Conversion Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Conversion Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Offer to Exchange Rate</p>
                        <p className="text-2xl font-bold">{analytics?.offer_to_exchange_rate || 0}%</p>
                      </div>
                      <Badge variant={analytics?.offer_to_exchange_rate >= 70 ? "default" : "secondary"}>
                        {analytics?.offer_to_exchange_rate >= 70 ? "Good" : "Average"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Exchange to Completion</p>
                        <p className="text-2xl font-bold">{analytics?.exchange_to_completion_rate || 0}%</p>
                      </div>
                      <Badge variant={analytics?.exchange_to_completion_rate >= 90 ? "default" : "secondary"}>
                        {analytics?.exchange_to_completion_rate >= 90 ? "Excellent" : "Good"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Days to Exchange</p>
                        <p className="text-2xl font-bold">{analytics?.avg_days_to_exchange || 0}</p>
                      </div>
                      <Clock className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Days to Completion</p>
                        <p className="text-2xl font-bold">{analytics?.avg_days_to_completion || 0}</p>
                      </div>
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Year-to-Date Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Year-to-Date Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-emerald-700">{analytics?.completed_ytd || 0}</p>
                      <p className="text-sm text-muted-foreground">Completed</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-blue-700">{analytics?.exchanged_cases || 0}</p>
                      <p className="text-sm text-muted-foreground">Exchanged</p>
                    </div>
                    <div className="p-4 bg-indigo-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-indigo-700">{analytics?.active_cases || 0}</p>
                      <p className="text-sm text-muted-foreground">Active</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-red-700">{analytics?.fallen_through_ytd || 0}</p>
                      <p className="text-sm text-muted-foreground">Fallen Through</p>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Total Value Handled</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency((analytics?.pipeline_value || 0) + (analytics?.completed_value || 0))}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SolicitorAnalyticsPage;
